import { useCallback } from 'react';
import { useTabs } from '../../../../context/TabsContext';

/**
 * URL-wrapped navigation handlers — user actions that update both app state and URL.
 */
export function useUrlWrappedHandlers({
  preferences,
  openDocsArea,
  openDashboardsArea,
  handleSelectSprintAndUpdateTab,
  handleSelectTemplateAndUpdateTab,
  handleSelectViewAndUpdateTab,
  handleTabClick,
  handleTabViewModeChange,
  navigateToCampaign,
  navigateToTemplate,
  navigateToView,
  navigateToDocs,
  navigateToDashboards,
  navigateToCampaigns,
  navigateToNewTab,
  handleNewTab,
  updateViewMode,
  updateCustomViewId,
  isNavigatingHistoryRef,
}) {
  const { openDocsHomeTab, openDashboardsHomeTab } = useTabs();

  const handleSelectSprintWithUrl = useCallback(async (sprint, options = {}) => {
    const isTemplate = sprint?.isTemplate === true || sprint?.type === 'template';

    if (isTemplate) {
      const merged = await handleSelectTemplateAndUpdateTab(sprint, options);
      const viewMode = typeof merged?.view === 'string'
        ? merged.view
        : (merged?.view?.type || preferences.defaultView || 'flow');
      navigateToTemplate(sprint.id, { viewMode });
      return;
    }

    const merged = await handleSelectSprintAndUpdateTab(sprint, options);

    const fallbackVm = preferences.defaultView || 'flow';
    const v = merged.view;
    let viewMode = fallbackVm;
    let customViewId = null;

    if (typeof v === 'object' && v?.customViewId) {
      viewMode = v.type || fallbackVm;
      customViewId = v.customViewId;
    } else if (typeof v === 'object' && v?.type) {
      viewMode = v.type;
    } else if (typeof v === 'string') {
      viewMode = v;
    }

    navigateToCampaign(sprint.id, {
      viewMode,
      missionId: merged.missionId,
      customViewId,
    });
  }, [handleSelectSprintAndUpdateTab, handleSelectTemplateAndUpdateTab, navigateToCampaign, navigateToTemplate, preferences.defaultView]);

  const handleSelectTemplateWithUrl = useCallback(async (template, options = {}) => {
    const merged = await handleSelectTemplateAndUpdateTab(template, options);
    const viewMode = typeof merged?.view === 'string'
      ? merged.view
      : (merged?.view?.type || preferences.defaultView || 'flow');
    navigateToTemplate(template.id, { viewMode });
  }, [handleSelectTemplateAndUpdateTab, navigateToTemplate, preferences.defaultView]);

  const handleSelectViewWithUrl = useCallback((view) => {
    handleSelectViewAndUpdateTab(view);
    navigateToView(view.id, {
      viewMode: preferences.defaultView || 'flow',
    });
  }, [handleSelectViewAndUpdateTab, navigateToView, preferences.defaultView]);

  const handleOpenDocsWithUrl = useCallback((docId) => {
    openDocsArea?.();
    navigateToDocs();
    if (docId) {
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('fokus:open-doc', { detail: { docId } }));
      });
      return;
    }
    openDocsHomeTab();
    requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent('fokus:docs-home'));
    });
  }, [openDocsArea, navigateToDocs, openDocsHomeTab]);

  const handleOpenDashboardsWithUrl = useCallback(() => {
    openDashboardsArea?.();
    navigateToDashboards();
    openDashboardsHomeTab();
  }, [openDashboardsArea, navigateToDashboards, openDashboardsHomeTab]);

  const handleTabClickWithUrl = useCallback(async (tab) => {
    const shouldSyncUrl = await handleTabClick(tab);
    if (shouldSyncUrl === false) return;
    if (tab.type === 'campaign') {
      navigateToCampaign(tab.targetId, { viewMode: tab.viewMode, customViewId: tab.customViewId });
    } else if (tab.type === 'template') {
      navigateToTemplate(tab.targetId, { viewMode: tab.viewMode });
    } else if (tab.type === 'view') {
      navigateToView(tab.targetId, { viewMode: tab.viewMode });
    } else if (tab.type === 'blank') {
      navigateToNewTab();
    } else if (tab.type === 'doc' || tab.type === 'docs') {
      navigateToDocs();
    } else if (tab.type === 'dashboards') {
      navigateToDashboards();
    } else if (tab.type === 'campaigns') {
      navigateToCampaigns();
    }
  }, [handleTabClick, navigateToCampaign, navigateToTemplate, navigateToView, navigateToNewTab, navigateToDocs, navigateToDashboards, navigateToCampaigns]);

  const handleNewTabWithUrl = useCallback(() => {
    handleNewTab();
    navigateToNewTab();
  }, [handleNewTab, navigateToNewTab]);

  const handleViewModeChange = useCallback((newViewMode, customViewId = undefined) => {
    const skipHistory = isNavigatingHistoryRef.current;
    handleTabViewModeChange(newViewMode, customViewId, { skipHistory });
    updateViewMode(newViewMode, customViewId);
  }, [handleTabViewModeChange, updateViewMode, isNavigatingHistoryRef]);

  const handleCustomViewChange = useCallback((customViewId, viewMode) => {
    updateCustomViewId(customViewId, viewMode);
  }, [updateCustomViewId]);

  return {
    handleSelectSprintWithUrl,
    handleSelectTemplateWithUrl,
    handleSelectViewWithUrl,
    handleOpenDocsWithUrl,
    handleOpenDashboardsWithUrl,
    handleTabClickWithUrl,
    handleNewTabWithUrl,
    handleViewModeChange,
    handleCustomViewChange,
  };
}
